package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-223: a request about work, not an absence: it never blocks scheduling, so it lives in its own
 * collection (`work_requests`) that apps before SH-223 do not read (they would take anything in
 * `absences` as a day off).
 */
@Serializable
enum class WorkRequestKind {
    /** Praca zdalna: the day (or hours) worked from [WorkRequest.placeName]; the shift stays. */
    REMOTE_WORK,
    /** Wydarzenie służbowe, wyjazd, szkolenie: working time away from the workplace; shifts then clash (a warning). */
    WORK_EVENT,
    /** Nadgodziny: work above the norm, to do or done, outside the shifts' times; paid with the supplements. */
    OVERTIME;

    companion object {
        fun fromName(name: String?): WorkRequestKind? = entries.firstOrNull { it.name == name }
    }
}

@Serializable
data class WorkRequest(
    val id: String,
    val userId: String,
    val workplaceId: String,
    val kind: WorkRequestKind,
    val status: AbsenceStatus,
    /** "YYYY-MM-DD", both included. */
    val startDate: String,
    val endDate: String,
    /** "HH:mm", both or neither (whole days); overtime always has them. */
    val startTime: String? = null,
    val endTime: String? = null,
    /** The place from the workplace's shared list ([RequestPlace]); its name kept, so a renamed or removed place still reads. */
    val placeId: String? = null,
    val placeName: String? = null,
    val note: String? = null,
    val rejectionReason: String? = null,
    val processedBy: String? = null,
    val processedAt: String? = null,
    val metadata: AuditMetadata
) {
    val isHourly: Boolean get() = !startTime.isNullOrBlank() && !endTime.isNullOrBlank()
}

/** SH-223: a place to work from, shared by a workplace's people (`workplaces/{id}/work_places`). */
@Serializable
data class RequestPlace(
    val id: String,
    val name: String,
    val createdBy: String = "",
    val createdAt: String = ""
)

/**
 * SH-223: the workplace's numbers for requests where the law sets one (settings.requests); the
 * defaults are the Polish law's as of 2026 (docs/requests.md).
 */
@Serializable
data class RequestSettings(
    /** Krwiodawstwo: free days per donation, the donation day included (ustawa o publicznej służbie krwi art. 9). */
    val bloodDonationDays: Int = 2,
    /** Art. 188 KP: childcare, 2 days or 16 hours a calendar year. */
    val childcareDays: Int = 2,
    val childcareHours: Int = 16,
    /** Art. 148¹ KP: force majeure, 2 days or 16 hours a calendar year. */
    val forceMajeureDays: Int = 2,
    val forceMajeureHours: Int = 16,
    /** Art. 173¹ KP: carer's leave, 5 working days a calendar year. */
    val carerLeaveDays: Int = 5,
    /** ZUS care allowance: 60 days a year for children, 14 for other family, 60 in all (hints). */
    val careAllowanceChildDays: Int = 60,
    val careAllowanceFamilyDays: Int = 14,
    val careAllowanceTotalDays: Int = 60,
    /**
     * Each request type's rule by its name (AbsenceType or WorkRequestKind): switched off for the
     * workplace, or offered only on some contracts. A type not listed follows
     * [RequestTypeRule.defaultFor].
     */
    val types: Map<String, RequestTypeRule> = emptyMap(),
    /**
     * Which defaults the types without a stored rule follow: [RequestDefaults.LEGACY] for a
     * workplace from before SH-223 (no settings.requests), [RequestDefaults.RECOMMENDED] for one
     * created since.
     */
    val defaults: RequestDefaults = RequestDefaults.RECOMMENDED
) {
    /** The rule of the type named [name] (AbsenceType or WorkRequestKind name). */
    fun ruleOf(name: String): RequestTypeRule = types[name] ?: RequestTypeRule.defaultFor(name, defaults)

    /**
     * Whether the type named [name] is offered to someone on [contract] (the contract valid that
     * day; NOT_SET or unknown counts as an employment contract, as the payroll does).
     */
    fun allows(name: String, contract: SystemContractType?, contractTypeId: String? = null): Boolean = ruleOf(name).allows(contract, contractTypeId)

    /**
     * Which settings the request types follow, as the settings screen's header names them: the
     * defaults of a workplace from before SH-223, the recommended ones, or the owner's own. Only
     * the types' rules and the law's numbers count (the care allowance's hints included).
     */
    val state: RequestSettingsState
        get() {
            val names = RequestTypeRule.ALL_TYPE_NAMES
            val sameNumbers = clamped().copy(types = emptyMap(), defaults = RequestDefaults.RECOMMENDED) == RECOMMENDED
            return when {
                sameNumbers && names.all { ruleOf(it) == RequestTypeRule.defaultFor(it, RequestDefaults.RECOMMENDED) } -> RequestSettingsState.RECOMMENDED
                sameNumbers && defaults == RequestDefaults.LEGACY && names.all { ruleOf(it) == RequestTypeRule.defaultFor(it, RequestDefaults.LEGACY) } ->
                    RequestSettingsState.LEGACY_DEFAULTS
                else -> RequestSettingsState.CUSTOM
            }
        }

    /** The value of [limit] here. */
    operator fun get(limit: RequestLimit): Int = when (limit) {
        RequestLimit.BLOOD_DONATION_DAYS -> bloodDonationDays
        RequestLimit.CHILDCARE_DAYS -> childcareDays
        RequestLimit.CHILDCARE_HOURS -> childcareHours
        RequestLimit.FORCE_MAJEURE_DAYS -> forceMajeureDays
        RequestLimit.FORCE_MAJEURE_HOURS -> forceMajeureHours
        RequestLimit.CARER_LEAVE_DAYS -> carerLeaveDays
    }

    /** These settings with [limit] set to [value] (kept within the limit's bounds). */
    fun with(limit: RequestLimit, value: Int): RequestSettings {
        val v = value.coerceIn(limit.min, limit.max)
        return when (limit) {
            RequestLimit.BLOOD_DONATION_DAYS -> copy(bloodDonationDays = v)
            RequestLimit.CHILDCARE_DAYS -> copy(childcareDays = v)
            RequestLimit.CHILDCARE_HOURS -> copy(childcareHours = v)
            RequestLimit.FORCE_MAJEURE_DAYS -> copy(forceMajeureDays = v)
            RequestLimit.FORCE_MAJEURE_HOURS -> copy(forceMajeureHours = v)
            RequestLimit.CARER_LEAVE_DAYS -> copy(carerLeaveDays = v)
        }
    }

    /** Each number within sensible bounds (a mistyped 200 days would hide every limit). */
    fun clamped(): RequestSettings = RequestSettings(
        bloodDonationDays = bloodDonationDays.coerceIn(1, 7),
        childcareDays = childcareDays.coerceIn(0, 30),
        childcareHours = childcareHours.coerceIn(0, 240),
        forceMajeureDays = forceMajeureDays.coerceIn(0, 30),
        forceMajeureHours = forceMajeureHours.coerceIn(0, 240),
        carerLeaveDays = carerLeaveDays.coerceIn(0, 60),
        careAllowanceChildDays = careAllowanceChildDays.coerceIn(0, 366),
        careAllowanceFamilyDays = careAllowanceFamilyDays.coerceIn(0, 366),
        careAllowanceTotalDays = careAllowanceTotalDays.coerceIn(0, 366),
        types = types,
        defaults = defaults
    )

    companion object {
        /** A workplace from before SH-223: what its people could ask for stays as it was. */
        val LEGACY = RequestSettings(defaults = RequestDefaults.LEGACY)

        /** The recommended settings: every type's recommended rule and the law's numbers ("Restore recommended"). */
        val RECOMMENDED = RequestSettings()
    }
}

/** SH-223: which settings a workplace's request types follow (the settings screen's header). */
enum class RequestSettingsState {
    /** A workplace from before SH-223, untouched: the types it had stay on every contract. */
    LEGACY_DEFAULTS,
    /** The recommended rules and the law's numbers. */
    RECOMMENDED,
    /** The owner changed something. */
    CUSTOM
}

/**
 * SH-223: a number the law sets for one request type, as the type's settings change it: its
 * [typeName], whether it counts [hours] (or days), the [statutory] value and the bounds
 * [RequestSettings.clamped] keeps it in.
 */
enum class RequestLimit(val typeName: String, val hours: Boolean, val statutory: Int, val min: Int, val max: Int) {
    /** Ustawa o publicznej służbie krwi art. 9: the donation day and the next. */
    BLOOD_DONATION_DAYS("BLOOD_DONATION", false, 2, 1, 7),
    /** Art. 188 KP: 2 days or 16 hours a year. */
    CHILDCARE_DAYS("CHILDCARE", false, 2, 0, 30),
    CHILDCARE_HOURS("CHILDCARE", true, 16, 0, 240),
    /** Art. 148¹ KP: 2 days or 16 hours a year. */
    FORCE_MAJEURE_DAYS("FORCE_MAJEURE", false, 2, 0, 30),
    FORCE_MAJEURE_HOURS("FORCE_MAJEURE", true, 16, 0, 240),
    /** Art. 173¹ KP: 5 working days a year. */
    CARER_LEAVE_DAYS("CARER_LEAVE", false, 5, 0, 60);

    companion object {
        /** The numbers of the type named [typeName], in this order (days before hours). */
        fun of(typeName: String): List<RequestLimit> = entries.filter { it.typeName == typeName }
    }
}

/** SH-223: the defaults of the request types' rules. */
@Serializable
enum class RequestDefaults {
    /**
     * A workplace from before SH-223: the four types it already had (annual leave, leave on demand,
     * sick leave, force majeure) stay on every contract; the new Labour Code types are for
     * employment contracts, the types for everyone for everyone.
     */
    LEGACY,
    /** A workplace created since: the Labour Code's types for employment contracts only. */
    RECOMMENDED
}

/**
 * SH-223: one request type's rule at a workplace: [enabled] (off: not offered at all; requests
 * already sent stay) and the [contracts] it is offered on (null: every contract, the owner's own
 * contract types included). [contractTypeIds]: the owner's own contract types
 * (`owners/{uid}/contract_types/{id}`) it is offered on besides [contracts]; a person is on one
 * when their pay terms name its id. Apps before this field skip it (they read [contracts] only).
 */
@Serializable
data class RequestTypeRule(
    val enabled: Boolean = true,
    val contracts: Set<SystemContractType>? = null,
    val contractTypeIds: Set<String> = emptySet()
) {
    fun allows(contract: SystemContractType?, contractTypeId: String? = null): Boolean {
        if (!enabled) return false
        if (contracts == null) return true
        if (contractTypeId != null && contractTypeId in contractTypeIds) return true
        val on = contract?.takeIf { it != SystemContractType.NOT_SET } ?: SystemContractType.STANDARD_EMPLOYMENT
        return on in contracts
    }

    /** Every contract, also the owner's contract types added later. */
    val forAllContracts: Boolean get() = contracts == null

    /** Offered on at least one contract (a switched-on type must be). */
    val hasAnyContract: Boolean get() = contracts == null || contracts.isNotEmpty() || contractTypeIds.isNotEmpty()

    /**
     * Whether the contract [key] is ticked: a [SystemContractType] name, or the id of one of the
     * owner's contract types.
     */
    fun offersContract(key: String): Boolean =
        contracts == null || contracts.any { it.name == key } || key in contractTypeIds

    /**
     * This rule with the contract [key] ticked ([on]) or not. "Every contract" becomes the explicit
     * list first ([CONTRACTS] and [customIds], the owner's contract types now); when everything is
     * ticked again it is "every contract" again.
     */
    fun withContract(key: String, on: Boolean, customIds: Collection<String>): RequestTypeRule {
        val system = CONTRACTS.firstOrNull { it.name == key }
        var chosen = contracts ?: CONTRACTS.toSet()
        var custom = if (contracts == null) customIds.toSet() else contractTypeIds
        if (system != null) chosen = if (on) chosen + system else chosen - system
        else custom = if (on) custom + key else custom - key
        return withContracts(chosen, custom, customIds)
    }

    /** [contracts] and [custom] ticked; everything ticked is "every contract". */
    fun withContracts(chosen: Set<SystemContractType>, custom: Set<String>, customIds: Collection<String>): RequestTypeRule =
        if (chosen.containsAll(CONTRACTS) && custom.containsAll(customIds)) copy(contracts = null, contractTypeIds = emptySet())
        else copy(contracts = chosen, contractTypeIds = custom)

    companion object {
        /** The contracts a rule can list (NOT_SET is read as an employment contract). */
        val CONTRACTS = SystemContractType.entries.filter { it != SystemContractType.NOT_SET }

        /** Every request type's name (AbsenceType and WorkRequestKind). */
        val ALL_TYPE_NAMES: List<String> get() = AbsenceType.entries.map { it.name } + WorkRequestKind.entries.map { it.name }

        /**
         * The Labour Code's types (and overtime): employment contracts only. Remote work, work
         * events, sick leave and quarantine (as information) and unpaid leave (as unavailability):
         * every contract. The care allowance (ZUS): employment and civil-law contracts (a zlecenie
         * with the voluntary sickness insurance has it too).
         */
        private val EMPLOYMENT_ONLY = setOf(
            "VACATION", "ON_DEMAND", "CHILDCARE", "CARER_LEAVE", "FORCE_MAJEURE", "OCCASIONAL_LEAVE", "MATERNITY", "PATERNITY",
            "PARENTAL", "CHILDCARE_LEAVE", "TIME_OFF_IN_LIEU", "OVERTIME", "BLOOD_DONATION"
        )

        /** The four types every workplace had before SH-223. */
        val EXISTING_BEFORE = setOf("VACATION", "ON_DEMAND", "SICK_LEAVE", "FORCE_MAJEURE")

        fun defaultFor(name: String, defaults: RequestDefaults = RequestDefaults.RECOMMENDED): RequestTypeRule = when {
            defaults == RequestDefaults.LEGACY && name in EXISTING_BEFORE -> RequestTypeRule()
            else -> recommendedFor(name)
        }

        private fun recommendedFor(name: String): RequestTypeRule = when (name) {
            in EMPLOYMENT_ONLY -> RequestTypeRule(contracts = setOf(SystemContractType.STANDARD_EMPLOYMENT))
            "CARE_ALLOWANCE" -> RequestTypeRule(contracts = setOf(SystemContractType.STANDARD_EMPLOYMENT, SystemContractType.CIVIL_LAW_CONTRACT))
            else -> RequestTypeRule()
        }
    }
}
